import { useState } from "react";
import type { ControllerRenderProps, FieldValues, Path } from "react-hook-form";
import { useDebounce } from "@/hooks/use-debounce";
import { SearchableMultiSelect } from "./searchable-multi-select";

interface FilterFieldProps<TFieldValues extends FieldValues> {
  field: ControllerRenderProps<TFieldValues, Path<TFieldValues>>;
  disabled?: boolean;
  options?: { id: string; label: string }[];
}

// Categories matching seed data values
export const TOP_SELLING_CATEGORIES = [
  { id: "Beauty", label: "Beauty" },
  { id: "Fashion", label: "Fashion" },
  { id: "Health & Wellness", label: "Health & Wellness" },
  { id: "Food & Beverage", label: "Food & Beverage" },
  { id: "Lifestyle", label: "Lifestyle" },
  { id: "Sports & Fitness", label: "Sports & Fitness" },
];

export const TONE_AND_APPROACH = [
  { id: "enthusiastic", label: "Enthusiastic" },
  { id: "energetic", label: "Energetic" },
  { id: "conversational", label: "Conversational" },
  { id: "authentic", label: "Authentic" },
  { id: "educational", label: "Educational" },
  { id: "inspirational", label: "Inspirational" },
  { id: "storytelling", label: "Storytelling" },
  { id: "motivational", label: "Motivational" },
];

export const CONTENT_FOCUS = [
  { id: "reviews recommendations", label: "Reviews & Recommendations" },
  { id: "personal lifestyle", label: "Personal Lifestyle" },
  { id: "educational tutorials", label: "Educational Tutorials" },
  { id: "beauty fashion", label: "Beauty & Fashion" },
  { id: "fitness wellness", label: "Fitness & Wellness" },
  { id: "food cooking", label: "Food & Cooking" },
];

export const PRODUCTION_QUALITY = [
  { id: "High polish", label: "High Polish" },
  { id: "Medium polish", label: "Medium Polish" },
  { id: "Lo-fi", label: "Lo-fi" },
  { id: "Raw", label: "Raw" },
  { id: "Authentic", label: "Authentic" },
  { id: "Polished-casual", label: "Polished Casual" },
];

export const TopSellingCategoriesFilter = <TFieldValues extends FieldValues>({
  field,
  disabled = false,
  options,
}: FilterFieldProps<TFieldValues>) => {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);

  const allOptions = options ?? TOP_SELLING_CATEGORIES;
  const filtered = allOptions.filter((opt) => opt.label.toLowerCase().includes(debouncedSearch.toLowerCase()));

  return (
    <SearchableMultiSelect
      field={field}
      label="Top Selling Categories"
      search={search}
      setSearch={setSearch}
      options={filtered}
      isLoading={false}
      placeholder="Search categories..."
      emptyMessage="No categories found"
      disabled={disabled}
    />
  );
};

export const ToneAndApproachFilter = <TFieldValues extends FieldValues>({
  field,
  disabled = false,
  options,
}: FilterFieldProps<TFieldValues>) => {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);

  const allOptions = options ?? TONE_AND_APPROACH;
  const filtered = allOptions.filter((opt) => opt.label.toLowerCase().includes(debouncedSearch.toLowerCase()));

  return (
    <SearchableMultiSelect
      field={field}
      label="Tone & Approach"
      search={search}
      setSearch={setSearch}
      options={filtered}
      isLoading={false}
      placeholder="Search tone..."
      emptyMessage="No tones found"
      disabled={disabled}
    />
  );
};

export const ContentFocusFilter = <TFieldValues extends FieldValues>({
  field,
  disabled = false,
  options,
}: FilterFieldProps<TFieldValues>) => {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);

  const allOptions = options ?? CONTENT_FOCUS;
  const filtered = allOptions.filter((opt) => opt.label.toLowerCase().includes(debouncedSearch.toLowerCase()));

  return (
    <SearchableMultiSelect
      field={field}
      label="Content Focus"
      search={search}
      setSearch={setSearch}
      options={filtered}
      isLoading={false}
      placeholder="Search content focus..."
      emptyMessage="No content focus found"
      disabled={disabled}
    />
  );
};

export const ProductionQualityFilter = <TFieldValues extends FieldValues>({
  field,
  disabled = false,
  options,
}: FilterFieldProps<TFieldValues>) => {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);

  const allOptions = options ?? PRODUCTION_QUALITY;
  const filtered = allOptions.filter((opt) => opt.label.toLowerCase().includes(debouncedSearch.toLowerCase()));

  return (
    <SearchableMultiSelect
      field={field}
      label="Production Quality"
      search={search}
      setSearch={setSearch}
      options={filtered}
      isLoading={false}
      placeholder="Search quality..."
      emptyMessage="No quality options found"
      disabled={disabled}
    />
  );
};
